// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/SmartTef.tsx
//  Integração Smart TEF — Fase 0: credenciais da loja + prova de conexão.
//
//  Smart TEF é API cloud: o ERP cria uma ordem de pagamento que aparece no app
//  Smart POS do terminal. Aqui só cadastramos as credenciais da LOJA e
//  listamos os terminais — listar terminais NÃO cria ordem e NÃO cobra nada.
// ════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import {
  CreditCard, Settings, Loader2, RefreshCw, Smartphone,
  BatteryCharging, Battery, CheckCircle2, XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { apiClient, getApiErrorMessage } from '@/lib/apiClient';
import { toast } from '@/components/ui/sonner';

// ── Tipos ───────────────────────────────────────────────────────────────────

interface ConfigSmartTef {
  baseUrl: string | null;
  cnpjLoja: string | null;
  storeId: number | null;
  webhookUrl: string | null;
  serialPosPadrao: string | null;
  ambiente: number;
  ativo: boolean;
  temToken: boolean;
  temSubscriptionKey: boolean;
  temWebhookToken: boolean;
}

interface Terminal {
  terminalId: number | null;
  serialPos: string | null;
  nickname: string | null;
  ativo: boolean | null;
  controleVersao: string | null;
  nivelBateria: number | null;
  carregando: boolean | null;
  storeId: number | null;
  cnpj: string | null;
}

const BASE_URL_PADRAO = 'https://api.smarttef.mobi';

// ── Página ──────────────────────────────────────────────────────────────────

export default function SmartTef() {
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [terminais, setTerminais] = useState<Terminal[] | null>(null);

  const [temToken, setTemToken] = useState(false);
  const [temSubscriptionKey, setTemSubscriptionKey] = useState(false);
  const [temWebhookToken, setTemWebhookToken] = useState(false);

  const [form, setForm] = useState({
    baseUrl: BASE_URL_PADRAO,
    tokenLoja: '',
    subscriptionKey: '',
    cnpjLoja: '',
    storeId: '',
    webhookUrl: '',
    webhookToken: '',
    serialPosPadrao: '',
    ambiente: '1',
    ativo: true,
  });

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await apiClient.get<ConfigSmartTef>('/api/smart-tef/config');
      setForm({
        baseUrl:         data.baseUrl ?? BASE_URL_PADRAO,
        tokenLoja:       '',   // segredo nunca volta da API
        subscriptionKey: '',
        cnpjLoja:        data.cnpjLoja ?? '',
        storeId:         data.storeId != null ? String(data.storeId) : '',
        webhookUrl:      data.webhookUrl ?? '',
        webhookToken:    '',
        serialPosPadrao: data.serialPosPadrao ?? '',
        ambiente:        String(data.ambiente ?? 1),
        ativo:           data.ativo,
      });
      setTemToken(data.temToken);
      setTemSubscriptionKey(data.temSubscriptionKey);
      setTemWebhookToken(data.temWebhookToken);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  const salvar = async () => {
    setSalvando(true);
    try {
      await apiClient.put('/api/smart-tef/config', {
        baseUrl:         form.baseUrl.trim() || BASE_URL_PADRAO,
        tokenLoja:       form.tokenLoja.trim() || null,
        subscriptionKey: form.subscriptionKey.trim() || null,
        cnpjLoja:        form.cnpjLoja.trim() || null,
        storeId:         form.storeId.trim() ? Number(form.storeId) : null,
        webhookUrl:      form.webhookUrl.trim() || null,
        webhookToken:    form.webhookToken.trim() || null,
        serialPosPadrao: form.serialPosPadrao.trim() || null,
        ambiente:        Number(form.ambiente),
        ativo:           form.ativo,
      });
      toast.success('Configuração do Smart TEF salva.');
      await carregar();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSalvando(false);
    }
  };

  const testarConexao = async () => {
    setTestando(true);
    setTerminais(null);
    try {
      const { data } = await apiClient.get<Terminal[]>('/api/smart-tef/terminais');
      setTerminais(data);
      toast.success(
        data.length === 0
          ? 'Credenciais aceitas — nenhum terminal cadastrado na loja ainda.'
          : `Conexão OK — ${data.length} terminal(is) encontrado(s).`);
    } catch (e) {
      setTerminais([]);
      toast.error(getApiErrorMessage(e));
    } finally {
      setTestando(false);
    }
  };

  const terminalPadrao = form.serialPosPadrao.trim()
    ? terminais?.find(t => t.serialPos === form.serialPosPadrao.trim()) ?? null
    : null;

  const credenciaisCompletas =
    (temToken || form.tokenLoja.trim() !== '') &&
    (temSubscriptionKey || form.subscriptionKey.trim() !== '');

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <CreditCard className="h-6 w-6" />
        <div>
          <h1 className="text-2xl font-semibold">Smart TEF</h1>
          <p className="text-sm text-muted-foreground">
            Pagamento por cartão integrado ao PDV via terminal Smart POS.
          </p>
        </div>
      </div>

      {/* ── Configuração ─────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Settings className="h-4 w-4" /> Credenciais da loja
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {carregando ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="baseUrl">URL base</Label>
                  <Input
                    id="baseUrl"
                    value={form.baseUrl}
                    onChange={e => setForm(f => ({ ...f, baseUrl: e.target.value }))}
                    placeholder={BASE_URL_PADRAO}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ambiente">Ambiente</Label>
                  <Select
                    value={form.ambiente}
                    onValueChange={v => setForm(f => ({ ...f, ambiente: v }))}
                  >
                    <SelectTrigger id="ambiente"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Produção</SelectItem>
                      <SelectItem value="2">Homologação</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tokenLoja">
                    Token da loja (Bearer)
                    {temToken && (
                      <Badge variant="outline" className="ml-2">já cadastrado</Badge>
                    )}
                  </Label>
                  <Input
                    id="tokenLoja"
                    type="password"
                    autoComplete="off"
                    value={form.tokenLoja}
                    onChange={e => setForm(f => ({ ...f, tokenLoja: e.target.value }))}
                    placeholder={temToken ? 'Deixe vazio para manter o atual' : 'Token recebido na criação da loja'}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subscriptionKey">
                    Ocp-Apim-Subscription-Key
                    {temSubscriptionKey && (
                      <Badge variant="outline" className="ml-2">já cadastrada</Badge>
                    )}
                  </Label>
                  <Input
                    id="subscriptionKey"
                    type="password"
                    autoComplete="off"
                    value={form.subscriptionKey}
                    onChange={e => setForm(f => ({ ...f, subscriptionKey: e.target.value }))}
                    placeholder={temSubscriptionKey ? 'Deixe vazio para manter a atual' : 'Chave recebida na criação da loja'}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cnpjLoja">CNPJ da loja</Label>
                  <Input
                    id="cnpjLoja"
                    value={form.cnpjLoja}
                    onChange={e => setForm(f => ({ ...f, cnpjLoja: e.target.value }))}
                    placeholder="Somente conferência"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="serialPosPadrao">Terminal padrão</Label>
                  <Input
                    id="serialPosPadrao"
                    value={form.serialPosPadrao}
                    onChange={e => setForm(f => ({ ...f, serialPosPadrao: e.target.value }))}
                    placeholder="Opcional — usado quando o caixa não escolher"
                  />
                  <p className="text-xs text-muted-foreground">
                    {terminalPadrao
                      ? <>Terminal: <strong>{terminalPadrao.nickname || 'Sem apelido'}</strong></>
                      : 'Clique em "Testar conexão" e escolha o terminal na lista — não precisa digitar o serial.'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="webhookUrl">URL do webhook (HTTPS)</Label>
                  <Input
                    id="webhookUrl"
                    value={form.webhookUrl}
                    onChange={e => setForm(f => ({ ...f, webhookUrl: e.target.value }))}
                    placeholder="https://api.suaempresa.com/api/smart-tef-webhook"
                  />
                  <p className="text-xs text-muted-foreground">
                    Só entra em uso na Fase 2 (recepção do resultado do pagamento).
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="webhookToken">
                    Token do webhook
                    {temWebhookToken && (
                      <Badge variant="outline" className="ml-2">já cadastrado</Badge>
                    )}
                  </Label>
                  <Input
                    id="webhookToken"
                    type="password"
                    autoComplete="off"
                    value={form.webhookToken}
                    onChange={e => setForm(f => ({ ...f, webhookToken: e.target.value }))}
                    placeholder={temWebhookToken ? 'Deixe vazio para manter o atual' : 'authorization_token do callback'}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="ativo"
                  checked={form.ativo}
                  onCheckedChange={v => setForm(f => ({ ...f, ativo: v === true }))}
                />
                <Label htmlFor="ativo" className="font-normal">
                  Integração ativa para esta empresa
                </Label>
              </div>

              <div className="flex gap-2">
                <Button onClick={salvar} disabled={salvando}>
                  {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Salvar
                </Button>
                <Button
                  variant="outline"
                  onClick={testarConexao}
                  disabled={testando || !credenciaisCompletas}
                  title={credenciaisCompletas ? undefined : 'Cadastre token e subscription key primeiro'}
                >
                  {testando
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <RefreshCw className="mr-2 h-4 w-4" />}
                  Testar conexão
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Terminais ────────────────────────────────────────────────────── */}
      {terminais !== null && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Smartphone className="h-4 w-4" /> Terminais da loja
            </CardTitle>
          </CardHeader>
          <CardContent>
            {terminais.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nenhum terminal retornado. Se a conexão foi aceita, a loja ainda não tem
                Smart POS ativado.
              </p>
            ) : (
              <div className="space-y-2">
                {terminais.map((t, i) => (
                  <div
                    key={t.terminalId ?? t.serialPos ?? i}
                    className="flex items-center justify-between rounded-md border p-3"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{t.nickname || 'Sem apelido'}</span>
                        {t.ativo === true
                          ? <Badge className="bg-green-100 text-green-800 border-green-200">
                              <CheckCircle2 className="mr-1 h-3 w-3" />Ativo
                            </Badge>
                          : t.ativo === false
                            ? <Badge variant="destructive">
                                <XCircle className="mr-1 h-3 w-3" />Inativo
                              </Badge>
                            : null}
                      </div>
                      <p className="truncate text-xs text-muted-foreground">
                        serial {t.serialPos ?? '—'}
                        {t.controleVersao ? ` · app ${t.controleVersao}` : ''}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      {t.nivelBateria != null && (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          {t.carregando
                            ? <BatteryCharging className="h-4 w-4" />
                            : <Battery className="h-4 w-4" />}
                          {t.nivelBateria}%
                        </div>
                      )}
                      {t.serialPos && (t.serialPos === form.serialPosPadrao.trim()
                        ? <Badge variant="outline">Padrão</Badge>
                        : <Button size="sm" variant="outline"
                            onClick={() => setForm(f => ({ ...f, serialPosPadrao: t.serialPos! }))}>
                            Usar como padrão
                          </Button>)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
